import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AthleteService } from '../services/athlete.service';
import { WEIGHT_CLASSES } from '../utils/weight-classes';
import { BRAZIL_STATES } from '../utils/brazil-location';
import { LocationService } from '../services/location.service';
import { URUGUAY_STATES } from '../utils/uruguay-location';

interface DraftPayload {
  data: any;
  step: number;
  expiresAt: number;
}

@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss'],
})
export class LandingComponent implements OnInit {
  weightClasses = WEIGHT_CLASSES;
  countries = [
    'Brasil',
    'Argentina',
    'Uruguai',
    'Chile',
    'Paraguai',
    'Bolívia',
    'Peru',
    'Equador',
    'Colômbia',
    'Venezuela'
  ];
  states = BRAZIL_STATES;
  cities: string[] = [];
  form!: FormGroup;

  steps = ['E-mail', 'Cadastro', 'Profissional', 'Contato'];
  currentStep = 0;
  saving = false;
  successMessage = '';
  errorMessage = '';
  lastSavedAt?: Date;
  stepError = '';
  showSuccessScreen = false;
  athleteExists = false;
  checkingEmail = false;

  private readonly storageKey = 'bfc_athlete_draft';
  private readonly expiryMs = 60 * 60 * 1000; // 1h

  constructor(
    private fb: FormBuilder,
    private athleteService: AthleteService,
    private locationService: LocationService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      full_name: ['', Validators.required],
      birth_date: ['', Validators.required],
      gender: ['', Validators.required],
      fight_name: ['', Validators.required],
      weight_category: ['', Validators.required],
      country: ['Brasil', Validators.required],
      state: ['', Validators.required],
      city: [{ value: '', disabled: true }, Validators.required],
      is_rookie: [false],
      victories: [null, [Validators.min(0)]],
      defeats: [null, [Validators.min(0)]],
      draws: [null, [Validators.min(0)]],
      notes: ['', [Validators.maxLength(200)]],
      tapology_link: ['', [Validators.required, Validators.pattern(/^https?:\/\/.+$/i)]],
      sherdog_link: ['', [Validators.required, Validators.pattern(/^https?:\/\/.+$/i)]],
      instagram: ['', [Validators.required, Validators.pattern(/^https?:\/\/.+$/i)]],
      phone: ['', [Validators.required, Validators.pattern(/^[0-9()+\-\s]{8,20}$/)]],
      email: ['', [Validators.required, Validators.email]],
    });

    this.loadDraft();
    this.form.valueChanges.subscribe(() => this.saveDraft(false));
    this.onRookieChange();
    this.ensureCountry();
    this.onCountryChange(true);
    this.form.get('email')?.valueChanges.subscribe(() => {
      this.athleteExists = false;
      this.stepError = '';
    });
  }

  onStateChange() {
    const country = this.form.get('country')?.value;
    const uf = this.form.get('state')?.value;
    const currentCity = this.form.get('city')?.value;

    if (country !== 'Brasil') {
      // Para outros países, cidade é campo livre.
      this.cities = [];
      this.form.get('city')?.enable();
      return;
    }

    if (!uf) {
      this.cities = [];
      this.form.get('city')?.reset();
      this.form.get('city')?.disable();
      this.form.get('city')?.updateValueAndValidity();
      return;
    }

    this.locationService.getCitiesByState(uf).subscribe({
      next: (cities) => {
        this.cities = cities;
        this.form.get('city')?.enable();
        this.form.get('city')?.updateValueAndValidity();

        if (currentCity && cities.includes(currentCity)) {
          this.form.get('city')?.setValue(currentCity);
        }
      },
      error: () => {
        this.cities = [];
        this.form.get('city')?.reset();
        this.form.get('city')?.disable();
        this.form.get('city')?.updateValueAndValidity();
      },
    });
  }

  onRookieChange() {
    const rookie = this.form.get('is_rookie')?.value;
    const victoryCtrl = this.form.get('victories');
    const defeatCtrl = this.form.get('defeats');
    const drawCtrl = this.form.get('draws');
    const tapologyCtrl = this.form.get('tapology_link');
    const sherdogCtrl = this.form.get('sherdog_link');

    if (rookie) {
      victoryCtrl?.setValue(null);
      defeatCtrl?.setValue(null);
      drawCtrl?.setValue(null);
      tapologyCtrl?.setValue('');
      sherdogCtrl?.setValue('');
      tapologyCtrl?.clearValidators();
      sherdogCtrl?.clearValidators();
      victoryCtrl?.setValidators([Validators.min(0)]);
      defeatCtrl?.setValidators([Validators.min(0)]);
      drawCtrl?.setValidators([Validators.min(0)]);
    } else {
      victoryCtrl?.setValidators([Validators.min(0)]);
      defeatCtrl?.setValidators([Validators.min(0)]);
      drawCtrl?.setValidators([Validators.min(0)]);
      tapologyCtrl?.setValidators([Validators.required, Validators.pattern(/^https?:\/\/.+$/i)]);
      sherdogCtrl?.setValidators([Validators.required, Validators.pattern(/^https?:\/\/.+$/i)]);
    }

    victoryCtrl?.updateValueAndValidity();
    defeatCtrl?.updateValueAndValidity();
    drawCtrl?.updateValueAndValidity();
    tapologyCtrl?.updateValueAndValidity();
    sherdogCtrl?.updateValueAndValidity();
  }

  nextStep() {
    if (this.currentStep === 0) {
      const emailCtrl = this.form.get('email');
      if (emailCtrl?.invalid) {
        emailCtrl.markAsTouched();
        this.stepError = 'Informe um e-mail válido.';
        return;
      }
      this.checkAthleteExists();
      return;
    }

    if (!this.isStepValid(this.currentStep)) {
      this.stepError = this.buildStepError(this.currentStep);
      this.markStepAsTouched(this.currentStep);
      return;
    }
    this.stepError = '';
    this.saveDraft();
    this.currentStep = Math.min(this.currentStep + 1, this.steps.length - 1);
  }

  prevStep() {
    this.currentStep = Math.max(this.currentStep - 1, 0);
    this.saveDraft(false);
  }

  submit() {
    if (!this.form.valid) {
      this.stepError = this.buildStepError(this.currentStep);
      this.markStepAsTouched(this.currentStep);
      return;
    }
    this.stepError = '';

    const rookie = this.form.get('is_rookie')?.value;
    const payload = this.form.getRawValue() as any;

    // Convert null values to 0 for victories, defeats, and draws
    payload.victories = payload.victories ?? 0;
    payload.defeats = payload.defeats ?? 0;
    payload.draws = payload.draws ?? 0;

    if (rookie) {
      payload.tapology_link = null;
      payload.sherdog_link = null;
    }

    this.saving = true;
    this.successMessage = '';
    this.errorMessage = '';

    this.athleteService.create(payload).subscribe({
      next: () => {
        this.successMessage = 'Cadastro salvo com sucesso!';
        this.saving = false;
        this.showSuccessScreen = true;
        this.scheduleReset();
      },
      error: () => {
        this.errorMessage = 'Não foi possível salvar agora. Tente novamente.';
        this.saving = false;
      },
    });
  }

  private scheduleReset() {
    // Reset form after 10 seconds
    setTimeout(() => {
      this.clearDraft();
      this.form.reset({
        country: 'Brasil',
        state: '',
        is_rookie: false,
        victories: null,
        defeats: null,
        draws: null,
      });
      this.form.get('city')?.reset();
      this.form.get('city')?.disable();
      this.onRookieChange();
      this.onCountryChange(true);
      this.stepError = '';
      this.errorMessage = '';
      this.successMessage = '';
      this.showSuccessScreen = false;
      this.currentStep = 0;
      this.athleteExists = false;
    }, 10000);
  }

  private isStepValid(step: number): boolean {
    const stepControls = this.getStepControls(step);
    return stepControls.every((ctrl) => {
      const control = this.form.get(ctrl);
      return !control || control.disabled || control.valid;
    });
  }

  private markStepAsTouched(step: number) {
    const stepControls = this.getStepControls(step);
    stepControls.forEach((ctrl) => this.form.get(ctrl)?.markAsTouched());
  }

  private buildStepError(step: number): string {
    const labels: Record<string, string> = {
      full_name: 'Nome completo',
      fight_name: 'Nome de luta',
      birth_date: 'Data de nascimento',
      gender: 'Gênero',
      weight_category: 'Categoria de peso',
      country: 'País',
      state: 'Estado',
      city: 'Cidade',
      tapology_link: 'Tapology',
      sherdog_link: 'Sherdog',
      instagram: 'Instagram',
      phone: 'Telefone',
      email: 'E-mail',
    };

    const missing = this.getStepControls(step)
      .map((ctrl) => ({ ctrl, control: this.form.get(ctrl) }))
      .filter(({ control }) => control && !control.disabled && control.invalid)
      .map(({ ctrl }) => labels[ctrl] ?? ctrl);

    if (!missing.length) return '';

    return `Preencha: ${missing.join(', ')}.`;
  }

  private getStepControls(step: number): string[] {
    if (step === 0) return ['email'];
    if (step === 1) return ['full_name', 'birth_date', 'country', 'state', 'city'];
    if (step === 2) {
      const rookie = this.form.get('is_rookie')?.value;
      if (rookie) return ['gender', 'fight_name', 'weight_category', 'is_rookie', 'victories', 'defeats', 'draws'];
      return ['gender', 'fight_name', 'weight_category', 'is_rookie', 'victories', 'defeats', 'draws'];
    }

    const rookie = this.form.get('is_rookie')?.value;
    const base = ['instagram', 'phone', 'email'];
    if (rookie) return base;
    return ['tapology_link', 'sherdog_link', ...base];
  }

  private saveDraft(updateTimestamp = true) {
    const payload: DraftPayload = {
      data: this.form.getRawValue(),
      step: this.currentStep,
      expiresAt: Date.now() + this.expiryMs,
    };
    localStorage.setItem(this.storageKey, JSON.stringify(payload));
    if (updateTimestamp) {
      this.lastSavedAt = new Date();
    }
  }

  private loadDraft() {
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) return;

    try {
      const draft: DraftPayload = JSON.parse(raw);
      if (draft.expiresAt < Date.now()) {
        this.clearDraft();
        return;
      }

      // Check if data is null or empty
      if (!draft.data || this.isDraftDataEmpty(draft.data)) {
        this.clearDraft();
        this.currentStep = 0;
        return;
      }

      this.form.patchValue(draft.data);
      this.ensureCountry();
      // Map old 5-step index to new 4-step (MFA step removed)
      const rawStep = draft.step ?? 0;
      this.currentStep = rawStep === 0 ? 0 : rawStep === 1 ? 1 : Math.min(rawStep - 1, this.steps.length - 1);
      this.lastSavedAt = new Date();

      // Reaplica dependências
      this.onStateChange();
      this.onRookieChange();
    } catch (e) {
      this.clearDraft();
      this.currentStep = 0;
    }
  }

  private isDraftDataEmpty(data: any): boolean {
    if (!data) return true;

    // If email is null/empty, consider draft empty (should start from step 0)
    const email = data.email;
    if (!email || email === null || email === undefined || email === '') {
      return true;
    }

    // Check if all other main fields are null or empty
    const mainFields = ['full_name', 'birth_date', 'gender', 'fight_name', 'weight_category'];
    const allNull = mainFields.every(field => {
      const value = data[field];
      return value === null || value === undefined || value === '';
    });

    return allNull;
  }

  private clearDraft() {
    localStorage.removeItem(this.storageKey);
    this.lastSavedAt = undefined;
  }

  private ensureCountry() {
    const countryCtrl = this.form.get('country');
    if (!countryCtrl?.value) {
      countryCtrl?.setValue('Brasil');
    }
  }

  get isBrazil(): boolean {
    return this.form.get('country')?.value === 'Brasil';
  }

  get emailValue(): string {
    return (this.form.get('email')?.value as string) ?? '';
  }

  onCountryChange(initial = false) {
    const country = this.form.get('country')?.value;
    const stateCtrl = this.form.get('state');
    const cityCtrl = this.form.get('city');
    const currentState = stateCtrl?.value;

    this.states = country === 'Uruguai' ? URUGUAY_STATES : country === 'Brasil' ? BRAZIL_STATES : [];
    const hasValidState =
      !!currentState &&
      ((country === 'Uruguai' && URUGUAY_STATES.includes(currentState)) ||
        (country === 'Brasil' && BRAZIL_STATES.includes(currentState)));

    if (!hasValidState) {
      stateCtrl?.setValue('');
      cityCtrl?.reset();
      this.cities = [];
    }

    if (country === 'Brasil') {
      cityCtrl?.disable();
      this.onStateChange();
    } else {
      // Demais países: cidade é campo livre (habilitada) e estado opcional texto.
      cityCtrl?.enable();
    }

    if (!initial) {
      stateCtrl?.markAsUntouched();
      cityCtrl?.markAsUntouched();
    }
  }

  get age(): number | null {
    const birth = this.form.get('birth_date')?.value;
    if (!birth) return null;
    const birthDate = new Date(birth);
    if (Number.isNaN(birthDate.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return Math.max(age, 0);
  }

  checkAthleteExists() {
    const email = this.emailValue;
    if (!email) return;

    this.checkingEmail = true;
    this.stepError = '';
    this.athleteExists = false;

    this.athleteService.checkByEmail(email).subscribe({
      next: (response) => {
        this.checkingEmail = false;
        const data = response?.data;
        if (data?.exists) {
          this.athleteExists = true;
        } else {
          this.stepError = '';
          this.currentStep = Math.min(this.currentStep + 1, this.steps.length - 1);
        }
        this.saveDraft();
      },
      error: () => {
        this.checkingEmail = false;
        this.athleteExists = false;
        this.stepError = '';
        this.saveDraft();
        this.currentStep = Math.min(this.currentStep + 1, this.steps.length - 1);
      },
    });
  }
}

